import RecipeImage from '../components/RecipeImage';
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { isFamilyFilterActive, getEditorIds, filterByFamily } from '../utils/familyFilter';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Container,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import '../App.css';
import { RECIPE_TYPES } from '../constants/Constants';

const VIEW_MODE_KEY = 'recipeViewMode';


const RecipeCardSkeleton = () => (
    <Card sx={{ height: 300, display: 'flex', flexDirection: 'column' }}>
        <Skeleton variant="rectangular" height={160} sx={{ flexShrink: 0 }} />
        <CardContent sx={{ flex: 1 }}>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
        </CardContent>
    </Card>
);

const RecipeList = () => {
    const [recipeData, setRecipeData] = useState(null);
    const [editorIds, setEditorIds] = useState(new Set());
    const [titleName, setTitleName] = useState('');
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(localStorage.getItem(VIEW_MODE_KEY) || 'grid');
    const { recipe_type } = useParams();

    const handleViewMode = (_, newMode) => {
        if (!newMode) return;
        setViewMode(newMode);
        localStorage.setItem(VIEW_MODE_KEY, newMode);
    };

    useEffect(() => {
        setLoading(true);
        setRecipeData(null);
    }, [recipe_type]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (!['editor', 'admin'].includes(user.role)) return;
        getEditorIds().then(setEditorIds);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const type = RECIPE_TYPES.find(t => t.key === recipe_type);
        setTitleName(type?.title || '');

        const api = `${process.env.REACT_APP_BACKEND_ADDRESS}/recipes?recipe_type=${recipe_type}`;
        const headers = { 'Authorization': 'Bearer ' + Cookies.get('token'), 'content-type': 'application/json' };

        async function fetchApi() {
            try {
                const data = await fetch(api, { method: 'GET', headers });
                const response = await data.json();
                setRecipeData(response);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchApi();
    }, [recipe_type]);

    const baseRecipes = recipeData?.recipes?.[recipe_type] || [];
    const recipes = (isFamilyFilterActive() ? filterByFamily(baseRecipes, editorIds) : baseRecipes)
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title));

    return (
        <Container component="main" sx={{ mt: 5, width: '90%', pb: 12 }}>
            <Box sx={{ mb: 3 }}>
                <Grid container alignItems="center">
                    <Grid item xs />
                    <Grid item>
                        <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>
                            {loading ? <Skeleton width={200} /> : (
                                <>
                                    {RECIPE_TYPES.find(t => t.key === recipe_type)?.icon}{' '}{titleName}
                                </>
                            )}
                        </Typography>
                    </Grid>
                    <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewMode} size="small">
                                <ToggleButton value="grid"><GridViewIcon fontSize="small" /></ToggleButton>
                                <ToggleButton value="list"><ListIcon fontSize="small" /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>
                </Grid>
                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mt: 1 }}>
                    <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewMode} size="small">
                        <ToggleButton value="grid"><GridViewIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="list"><ListIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {loading ? (
                <Grid container spacing={2}>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Grid item key={i} xs={6} sm={4} md={3}>
                            <RecipeCardSkeleton />
                        </Grid>
                    ))}
                </Grid>
            ) : recipes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
                    <MenuBookIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6">No recipes here yet</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Be the first to add one!
                    </Typography>
                </Box>
            ) : viewMode === 'grid' ? (
                <Grid container spacing={2}>
                    {recipes.map((recipe) => (
                        <Grid item key={recipe.id} xs={6} sm={4} md={3}>
                            <Link to={`/recipe/${recipe.id}`} state={{ id: recipe.id }}>
                                <Card sx={{ height: 300, display: 'flex', flexDirection: 'column' }}>
                                    <CardMedia
                                        component="img"
                                        loading="lazy"
                                        sx={{ height: 160, flexShrink: 0 }}
                                        image={recipe.download_image_url || '/anchovie.jpg'}
                                        alt={recipe.title}
                                        onError={(e) => { e.target.onerror = null; e.target.src = '/image-error.svg'; }}
                                    />
                                    <CardContent sx={{ flex: 1, overflow: 'hidden' }}>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 600, mb: 0.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
                                        >
                                            {recipe.title}
                                        </Typography>
                                        {(recipe.authors?.length > 0 || recipe.author) && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                                            >
                                                Recipe Courtesy of {recipe.authors?.length > 0
                                                    ? recipe.authors.map(a => a.display_name).join(' & ')
                                                    : recipe.author}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Link>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <List disablePadding>
                    {recipes.map((recipe) => (
                        <ListItem key={recipe.id} disablePadding divider>
                            <ListItemButton component={Link} to={`/recipe/${recipe.id}`} state={{ id: recipe.id }} sx={{ gap: 1.5 }}>
                                <RecipeImage
                                    loading="lazy"
                                    src={recipe.download_image_url || '/anchovie.jpg'}
                                    alt={recipe.title}
                                    sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                />
                                <ListItemText
                                    primary={recipe.title}
                                    secondary={recipe.authors?.length > 0
                                ? `Recipe Courtesy of ${recipe.authors.map(a => a.display_name).join(' & ')}`
                                : recipe.author ? `Recipe Courtesy of ${recipe.author}` : null}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </Container>
    );
};

export default RecipeList;
